
import { computed, defineComponent, PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { useMutation } from "@vue/apollo-composable";
import { logErrorMessages } from "@vue/apollo-util";
import { useToast } from "vue-toastification";

import enUK from "@/locales/project/en-UK.json";

import { CREATE_OFFERED } from "@/graphql/offered/mutations";

import FormItem from "@/components/form/FormItem.vue";
import Modal from "@/components/Modal.vue";
import BasicButton from "@/components/BasicButton.vue";

import { formatPrice, parsePriceToCents } from "@/helpers/utils";
import dayjs from "dayjs";
import { DocumentNode } from "graphql";

type MessageSchema = typeof enUK;

export default defineComponent({
  props: {
    projectId: {
      type: String,
      required: true,
    },
    refetchQueries: {
      type: Array as PropType<{ query: DocumentNode; variables?: object }[]>,
      required: false,
      default: () => [],
    },
    defaultAmountNet: {
      type: Number,
      required: false,
      default: 0,
    },
    defaultComment: {
      type: String,
      required: false,
      default: "",
    },
    creditNoteForId: {
      type: String,
      required: false,
    },
  },
  setup(props, context) {
    const { t } = useI18n<[MessageSchema], "en-UK">({
      useScope: "global",
      inheritLocale: true,
      messages: {
        "en-UK": enUK,
      },
    });

    const toast = useToast();

    const offeredId = ref("");
    const amountNet = ref(props.defaultAmountNet);
    const date = ref(dayjs().format("YYYY-MM-DD"));
    const comment = ref(props.defaultComment);

    const creationVariables = computed(() => ({
      projectId: props.projectId,
      amountNet: amountNet.value,
      date: date.value,
      comment: comment.value,
      creditNoteFor: props.creditNoteForId,
    }));

    const v$ = useVuelidate(
      {
        projectId: required,
        amountNet: required,
        date: required,
      },
      creationVariables,
      {
        $autoDirty: true,
      },
    );

    const {
      mutate: createOffered,
      onDone: onDoneCreateOffered,
      onError: onErrorCreateOffered,
      loading: loadingCreateOffered,
    } = useMutation(CREATE_OFFERED, () => ({
      variables: creationVariables.value,
      refetchQueries: props.refetchQueries,
      awaitRefetchQueries: true,
    }));

    onDoneCreateOffered(async (result) => {
      const response = result.data?.createOffered;

      if (response?.status === "ok") {
        offeredId.value = response.data?.id;
        toast.success(t("success.offered.add"));
        context.emit("created", response.data);
        context.emit("close");
        return;
      }

      const errorResponse = response?.error || t("errors.internal");

      toast.error(t("errors.offered.add", { message: errorResponse }));
    });

    onErrorCreateOffered((error) => {
      logErrorMessages(error);

      const message =
        error.graphQLErrors?.[0]?.message ||
        error.message ||
        t("errors.internal");

      toast.error(t("errors.offered.add", { message }));
    });

    const loading = computed(() => loadingCreateOffered.value);

    return {
      loading,
      createOffered,
      formatPrice,
      parsePriceToCents,
      amountNet,
      date,
      comment,
      t,
      v$,
    };
  },
  emits: ["close", "created"],
  methods: {
    async submitForm() {
      const isFormCorrect = await this.v$.$validate();
      if (isFormCorrect) {
        return this.createOffered();
      }
    },
    closeModal(): void {
      this.$emit("close");
    },
  },
  components: {
    Modal,
    FormItem,
    BasicButton,
  },
});
